import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import { fileShapeNative, fileShapeWeb } from '@gov-nx/core/hooks';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { toStringDate, useDateShape } from '@gov-nx/utils/common';
import type {
	OwnDocumentFormData,
	OwnDokumentDataRequest,
} from './context.types';

interface FormDefinitionProps {
	code: PageCode;
}

export function FormInstanceNative({
	code,
}: FormDefinitionProps): FormDefinition<OwnDocumentFormData> {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const { getDatePickerShape } = useDateShape();
	const tsn = getLocalizeCurried(code);

	const formSchema = yup
		.object({
			_replaceFile: yup.boolean(),
			nazev: yup
				.string()
				.max(255, tsn('formular.validace.nazev.max'))
				.required(tsn('formular.validace.nazev.povinny')),
			cisloDokumentu: yup
				.string()
				.max(255, tsn('formular.validace.cislo-dokumentu.max')),
			komentar: yup.string().max(255, tsn('formular.validace.komentar.max')),
			platnostOd: getDatePickerShape(),
			platnostDo: getDatePickerShape().min(
				yup.ref('platnostOd'),
				tsn('formular.validace.platnost-do.starsi-nez-platnost-od')
			),
			nazevSouboru: yup.string(),
			soubor: yup.array().of(fileShapeNative()).optional(),
		})
		.required();

	const formMethods = useGovForm<OwnDocumentFormData>({
		formSchema,
		defaultValues: {
			_replaceFile: false,
			nazev: undefined,
			nazevSouboru: undefined,
			platnostDo: undefined,
			platnostOd: undefined,
			cisloDokumentu: undefined,
			komentar: undefined,
			soubor: undefined,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export function FormInstanceWeb({
	code,
}: FormDefinitionProps): FormDefinition<OwnDocumentFormData> {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const { getDatePickerShape } = useDateShape();
	const tsn = getLocalizeCurried(code);

	const formSchema = yup
		.object({
			_replaceFile: yup.boolean(),
			nazev: yup
				.string()
				.max(255, tsn('formular.validace.nazev.max'))
				.required(tsn('formular.validace.nazev.povinny')),
			cisloDokumentu: yup
				.string()
				.max(255, tsn('formular.validace.cislo-dokumentu.max')),
			komentar: yup.string().max(255, tsn('formular.validace.komentar.max')),
			platnostOd: getDatePickerShape(),
			platnostDo: getDatePickerShape().min(
				yup.ref('platnostOd'),
				tsn('formular.validace.platnost-do.starsi-nez-platnost-od')
			),
			nazevSouboru: yup.string(),
			soubor: yup.array().of(fileShapeWeb()).optional(),
		})
		.required();

	const formMethods = useGovForm<OwnDocumentFormData>({
		formSchema,
		defaultValues: {
			_replaceFile: false,
			nazev: undefined,
			nazevSouboru: undefined,
			platnostDo: undefined,
			platnostOd: undefined,
			cisloDokumentu: undefined,
			komentar: undefined,
			soubor: undefined,
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export const prepareSubmitData = (formData: OwnDocumentFormData) => {
	let requestBody: OwnDokumentDataRequest = {
		nazev: formData.nazev,
		kategorie: '#DOKLAD#',
		zdroj: 'UZIVATELEM_NAHRANY_DOKUMENT',
	};

	if (formData.komentar) {
		requestBody = { ...requestBody, komentar: formData.komentar };
	}
	if (formData.platnostOd) {
		requestBody = {
			...requestBody,
			platnostOd: toStringDate(formData.platnostOd),
		};
	}
	if (formData.platnostDo) {
		requestBody = {
			...requestBody,
			platnostDo: toStringDate(formData.platnostDo),
		};
	}
	if (formData.cisloDokumentu) {
		requestBody = {
			...requestBody,
			cisloDokumentu: formData.cisloDokumentu,
		};
	}

	const hasFiles = !!formData.soubor?.length;

	if (hasFiles) {
		const soubory = prepareFiles(formData.soubor);

		requestBody = {
			...requestBody,
			soubory,
		};
	}

	return requestBody;
};

export const prepareFiles = (soubory: OwnDocumentFormData['soubor']) =>
	(
		soubory?.map((file) => {
			if (!file.fileId) {
				return undefined;
			}
			return {
				souborId: file.fileId,
				nazev: file.name,
			};
		}) ?? []
	).filter((item) => !!item) as {
		souborId: string;
		nazev: string;
	}[];
